import React,{useEffect,useLayoutEffect,useMemo,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCalendar,FiChevronLeft,FiChevronRight,FiX}=FiIcons;

const pad=(value)=> String(value).padStart(2,'0');
const toIso=(date)=> `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
const parseIso=(value)=> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
  const [year,month,day]=String(value).split('-').map(Number);
  const parsed=new Date(year,month-1,day);
  return parsed.getFullYear()===year && parsed.getMonth()===month-1 && parsed.getDate()===day ? parsed : null;
};
const formatDisplay=(value)=> {
  const parsed=parseIso(value);
  return parsed ? parsed.toLocaleDateString('en-AU',{day:'2-digit',month:'short',year:'numeric'}) : '';
};
const sameDay=(a,b)=> a && b && a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const addDays=(date,days)=> {
  const next=new Date(date.getFullYear(),date.getMonth(),date.getDate());
  next.setDate(next.getDate()+days);
  return next;
};

export default function SmartDatePicker({
  value='',
  onChange,
  disabled=false,
  placeholder='Select date',
  allowClear=true,
  presets=[],
  className='',
  ariaLabel='Select date',
}) {
  const triggerRef=useRef(null);
  const popoverRef=useRef(null);
  const selected=parseIso(value);
  const today=useMemo(()=> {
    const now=new Date();
    return new Date(now.getFullYear(),now.getMonth(),now.getDate());
  },[]);
  const [open,setOpen]=useState(false);
  const [viewDate,setViewDate]=useState(()=> selected || today);
  const [position,setPosition]=useState({mode:'floating',top:0,left:0,width:320});

  useEffect(()=> {
    const parsed=parseIso(value);
    if (parsed) setViewDate(parsed);
  },[value]);

  useEffect(()=> {
    if (!open) return undefined;
    const handlePointer=(event)=> {
      const target=event.target;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey=(event)=> {
      if (event.key==='Escape') setOpen(false);
    };
    document.addEventListener('mousedown',handlePointer);
    document.addEventListener('keydown',handleKey);
    return ()=> {
      document.removeEventListener('mousedown',handlePointer);
      document.removeEventListener('keydown',handleKey);
    };
  },[open]);

  useLayoutEffect(()=> {
    if (!open || !triggerRef.current) return undefined;

    const updatePosition=()=> {
      const trigger=triggerRef.current;
      if (!trigger) return;
      const viewportWidth=window.innerWidth;
      const viewportHeight=window.innerHeight;
      const mobile=viewportWidth<=640;

      if (mobile) {
        setPosition({mode:'sheet',top:0,left:0,width:0});
        return;
      }

      const rect=trigger.getBoundingClientRect();
      const width=Math.min(336,Math.max(310,viewportWidth-24));
      const measuredHeight=popoverRef.current?.offsetHeight || 414;
      const gap=8;
      const safeMargin=12;
      const spaceBelow=viewportHeight-rect.bottom-safeMargin;
      const spaceAbove=rect.top-safeMargin;
      const opensBelow=spaceBelow>=measuredHeight || spaceBelow>=spaceAbove;
      const rawTop=opensBelow ? rect.bottom+gap : rect.top-measuredHeight-gap;
      const maxTop=Math.max(safeMargin,viewportHeight-measuredHeight-safeMargin);
      const top=Math.min(Math.max(safeMargin,rawTop),maxTop);
      const left=Math.min(Math.max(safeMargin,rect.left),Math.max(safeMargin,viewportWidth-width-safeMargin));
      setPosition({mode:'floating',top,left,width});
    };

    updatePosition();
    const frame=window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize',updatePosition);
    window.addEventListener('scroll',updatePosition,true);
    return ()=> {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize',updatePosition);
      window.removeEventListener('scroll',updatePosition,true);
    };
  },[open,viewDate,presets.length,value]);

  const calendarDays=useMemo(()=> {
    const year=viewDate.getFullYear();
    const month=viewDate.getMonth();
    const firstDay=new Date(year,month,1);
    const mondayOffset=(firstDay.getDay()+6)%7;
    const gridStart=new Date(year,month,1-mondayOffset);
    return Array.from({length:42},(_,index)=> addDays(gridStart,index));
  },[viewDate]);

  const choose=(date)=> {
    onChange?.(toIso(date));
    setViewDate(date);
    setOpen(false);
  };

  const applyPreset=(preset)=> {
    const date=typeof preset.getDate==='function' ? preset.getDate(today) : addDays(today,preset.daysFromToday || 0);
    if (date instanceof Date && !Number.isNaN(date.getTime())) choose(date);
  };

  const moveMonth=(offset)=> setViewDate((current)=> new Date(current.getFullYear(),current.getMonth()+offset,1));
  const monthLabel=viewDate.toLocaleDateString('en-AU',{month:'long',year:'numeric'});

  const calendar=(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={`${ariaLabel} calendar`}
      onMouseDown={(event)=> event.stopPropagation()}
      className={`bg-white border border-slate-200 shadow-2xl shadow-slate-900/20 overflow-hidden ${position.mode==='sheet' ? 'rounded-t-3xl' : 'rounded-2xl'}`}
      style={position.mode==='sheet'
        ? {
            position:'fixed',
            zIndex:1100,
            left:'12px',
            right:'12px',
            bottom:'calc(env(safe-area-inset-bottom, 0px) + 12px)',
            maxHeight:'calc(100dvh - env(safe-area-inset-top, 0px) - 24px)',
          }
        : {
            position:'fixed',
            zIndex:1100,
            top:`${position.top}px`,
            left:`${position.left}px`,
            width:`${position.width}px`,
          }}
    >
      {position.mode==='sheet' && <div className="w-10 h-1 rounded-full bg-slate-300 mx-auto mt-2.5" />}
      <div className="px-4 py-3 bg-slate-900 text-white flex items-center justify-between">
        <button type="button" onClick={()=> moveMonth(-1)} className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10" aria-label="Previous month"><SafeIcon icon={FiChevronLeft} className="text-xl" /></button>
        <div className="text-sm font-semibold">{monthLabel}</div>
        <button type="button" onClick={()=> moveMonth(1)} className="w-11 h-11 rounded-xl flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10" aria-label="Next month"><SafeIcon icon={FiChevronRight} className="text-xl" /></button>
      </div>

      <div className="p-3 sm:p-4 overflow-y-auto">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day)=> <div key={day} className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 py-1">{day}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((date)=> {
            const inMonth=date.getMonth()===viewDate.getMonth();
            const isSelected=sameDay(date,selected);
            const isToday=sameDay(date,today);
            return (
              <button
                key={toIso(date)}
                type="button"
                onClick={()=> choose(date)}
                aria-label={date.toLocaleDateString('en-AU',{day:'numeric',month:'long',year:'numeric'})}
                aria-pressed={isSelected}
                className={`min-h-10 sm:min-h-9 rounded-lg text-sm sm:text-xs font-medium transition-colors ${
                  isSelected
                    ? 'bg-blue-600 text-white shadow-sm'
                    : isToday
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200'
                      : inMonth
                        ? 'text-slate-700 hover:bg-slate-100 active:bg-slate-200'
                        : 'text-slate-300 hover:bg-slate-50'
                }`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-slate-100 pt-3">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={()=> choose(today)} className="min-h-10 px-3 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-100">Today</button>
          {presets.map((preset)=> (
            <button key={preset.label} type="button" onClick={()=> applyPreset(preset)} className="min-h-10 px-3 rounded-lg text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200">{preset.label}</button>
          ))}
          {allowClear && value && (
            <button type="button" onClick={()=> {onChange?.('');setOpen(false);}} className="min-h-10 ml-auto px-3 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 inline-flex items-center gap-1"><SafeIcon icon={FiX} /> Clear</button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div ref={triggerRef} className={className}>
      <button
        type="button"
        disabled={disabled}
        onClick={()=> setOpen((current)=> !current)}
        aria-label={ariaLabel}
        aria-expanded={open}
        className="w-full min-h-[44px] flex items-center justify-between gap-3 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-left outline-none transition-all hover:border-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
      >
        <span className={value ? 'font-medium text-slate-800' : 'text-slate-400'}>{formatDisplay(value) || placeholder}</span>
        <SafeIcon icon={FiCalendar} className="text-blue-600 flex-shrink-0" />
      </button>
      {open && !disabled && createPortal(
        position.mode==='sheet'
          ? <div className="fixed inset-0 z-[1090] bg-slate-950/25 backdrop-blur-[1px]" onMouseDown={()=> setOpen(false)}>{calendar}</div>
          : calendar,
        document.body
      )}
    </div>
  );
}